'use client';

import { Component, ErrorInfo, ReactNode } from 'react';

import { ErrorEventsEnum, errorLogToRemoteUtil } from '@northladder/utilities';
import { logErrorToSentry } from '@northladder/web-utils';

import { FallbackUI } from './FallbackUI';

export interface IAppErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface IAppErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * -----------------------------------------------------------------------------
 * Custom error boundary to catch errors in the UI and log them to remote
 */
export class AppErrorBoundary extends Component<
  IAppErrorBoundaryProps,
  IAppErrorBoundaryState
> {
  constructor(props: IAppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(_: Error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  // If an error in a child is encountered, this will run
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Catch errors in any components below and re-render with error message
    this.setState({ hasError: true, error, errorInfo });

    errorLogToRemoteUtil({
      error,
      errorCode: ErrorEventsEnum.ERROR_IN_APP,
      // @ts-ignore
      params: { errorInfo },
    });
    logErrorToSentry({
      error,
      errorInfo,
      errorKey: ErrorEventsEnum.ERROR_IN_APP,
      errorName: error.name,
      source: 'AppErrorBoundary.componentDidCatch',
    });
  }

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      if (fallback) return fallback;

      return <FallbackUI error={error} errorInfo={errorInfo} />;
    }

    return children;
  }
}
